/**
 * Unified AI Service for Lucid Cyber RPG
 * Consolidates text generation, TTS/STT, and image generation
 */

import { GameState, StorySegment, CombatState, GameEvent } from '../types/gameTypes';
import { PollinationsService } from './pollinationsService';

// Service Configuration
const CONFIG = {
  OPENROUTER_API_KEY: import.meta.env.VITE_OPENROUTER_API_KEY || '',
  OPENROUTER_MODEL: import.meta.env.VITE_OPENROUTER_DEFAULT_MODEL || 'meta-llama/llama-4-maverick:free',
  ELEVENLABS_API_KEY: import.meta.env.VITE_ELEVENLABS_API_TOKEN || import.meta.env.ELEVENLABS_API_TOKEN || '',
  ELEVENLABS_VOICE_ID: 'EXAVITQu4vr4xnSDxMaL',
  HUGGINGFACE_API_KEY: import.meta.env.VITE_HUGGING_FACE_HUB_TOKEN || import.meta.env.HUGGING_FACE_HUB_TOKEN || '',
  HUGGINGFACE_MODEL: 'black-forest-labs/FLUX.1-schnell'
};

// OpenRouter API Base URL
const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1';
const ELEVENLABS_API_URL = 'https://api.elevenlabs.io/v1';
const HUGGINGFACE_API_URL = 'https://api-inference.huggingface.co/models';

// Types
interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface GeneratedContent {
  text: string;
  audioUrl?: string;
  imageUrl?: string;
}

class UnifiedAIService {
  private static instance: UnifiedAIService;

  static getInstance(): UnifiedAIService {
    if (!this.instance) {
      this.instance = new UnifiedAIService();
    }
    return this.instance;
  }

  constructor() {
    this.validateConfiguration();
  }

  private validateConfiguration() {
    if (!CONFIG.OPENROUTER_API_KEY) {
      console.warn('[UnifiedAI] OpenRouter API key not configured');
    }
    if (!CONFIG.ELEVENLABS_API_KEY) {
      console.warn('[UnifiedAI] ElevenLabs API key not configured');
    }
    if (!CONFIG.HUGGINGFACE_API_KEY) {
      console.warn('[UnifiedAI] HuggingFace API key not configured');
    }
  }

  /**
   * Generate text content using OpenRouter
   */
  async generateText(
    prompt: string,
    systemPrompt?: string,
    temperature: number = 0.8
  ): Promise<string> {
    try {
      const messages: ChatMessage[] = [];

      if (systemPrompt) {
        messages.push({ role: 'system', content: systemPrompt });
      }
      messages.push({ role: 'user', content: prompt });

      const response = await fetch(`${OPENROUTER_API_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CONFIG.OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Lucid Cyber RPG'
        },
        body: JSON.stringify({
          model: CONFIG.OPENROUTER_MODEL,
          messages,
          temperature,
          max_tokens: 500,
          stream: false
        })
      });

      if (!response.ok) {
        console.error('[UnifiedAI] OpenRouter API error:', response.status);
        return this.getFallbackResponse(prompt);
      }

      const data = await response.json();
      const content = data.choices?.[0]?.message?.content || '';

      // Clean up any JSON formatting artifacts
      return this.cleanTextResponse(content);
    } catch (error) {
      console.error('[UnifiedAI] Text generation error:', error);
      return this.getFallbackResponse(prompt);
    }
  }

  /**
   * Generate audio from text using ElevenLabs TTS
   */
  async generateAudio(text: string, agentType?: string): Promise<string | null> {
    if (!CONFIG.ELEVENLABS_API_KEY) {
      return null;
    }

    try {
      // Use the new ElevenLabs service with agent support
      const { elevenLabsService } = await import('./elevenlabsService');

      if (agentType) {
        // Use agent-based generation
        const response = await elevenLabsService.generateWithAgent(agentType, text);
        return response.audioUrl || null;
      } else {
        // Use direct TTS
        const audioBuffer = await elevenLabsService.textToSpeech(text);
        const base64Audio = btoa(
          new Uint8Array(audioBuffer).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );
        return `data:audio/mpeg;base64,${base64Audio}`;
      }
    } catch (error) {
      console.error('[UnifiedAI] Audio generation error:', error);
      return null;
    }
  }

  /**
   * Generate image with robust fallback cascade
   */
  async generateImage(prompt: string, style: string = 'cyberpunk'): Promise<string | null> {
    const enhancedPrompt = `${style} style, ${prompt}, high quality, detailed, 4k`;
    console.log('[UnifiedAI] Starting image generation for:', enhancedPrompt);

    // Strategy 1: Try HuggingFace (5 second timeout)
    if (CONFIG.HUGGINGFACE_API_KEY) {
      try {
        console.log('[UnifiedAI] Attempting HuggingFace generation...');
        const huggingFaceUrl = await this.generateImageHuggingFace(enhancedPrompt);
        if (huggingFaceUrl) {
          console.log('[UnifiedAI] ✓ HuggingFace generation successful');
          return huggingFaceUrl;
        }
      } catch (error) {
        console.warn('[UnifiedAI] ✗ HuggingFace failed:', error);
      }
    } else {
      console.log('[UnifiedAI] HuggingFace API key not configured, skipping');
    }

    // Strategy 2: Try TogetherAI (5 second timeout)
    const togetherApiKey = import.meta.env.VITE_TOGETHER_API_KEY || import.meta.env.VITE_TOGETHER_AI_KEY;
    if (togetherApiKey) {
      try {
        console.log('[UnifiedAI] Attempting TogetherAI generation...');
        const togetherUrl = await this.generateImageTogetherAI(enhancedPrompt, togetherApiKey);
        if (togetherUrl) {
          console.log('[UnifiedAI] ✓ TogetherAI generation successful');
          return togetherUrl;
        }
      } catch (error) {
        console.warn('[UnifiedAI] ✗ TogetherAI failed:', error);
      }
    } else {
      console.log('[UnifiedAI] TogetherAI API key not configured, skipping');
    }

    // Strategy 3: Pollinations (ALWAYS works - no timeout needed)
    console.log('[UnifiedAI] Using Pollinations as final fallback...');
    const pollinationsUrl = await this.generateImagePollinations(prompt);
    console.log('[UnifiedAI] ✓ Pollinations fallback successful');
    return pollinationsUrl;
  }

  /**
   * Create timeout wrapper for fetch requests
   */
  private async fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number = 5000): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Generate image using HuggingFace with proper CORS and timeout handling
   */
  private async generateImageHuggingFace(prompt: string): Promise<string | null> {
    try {
      const response = await this.fetchWithTimeout(
        `${HUGGINGFACE_API_URL}/${CONFIG.HUGGINGFACE_MODEL}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${CONFIG.HUGGINGFACE_API_KEY}`,
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
            'Access-Control-Request-Method': 'POST',
            'Access-Control-Request-Headers': 'authorization,content-type'
          },
          body: JSON.stringify({
            inputs: prompt,
            parameters: {
              guidance_scale: 7.5,
              num_inference_steps: 4,
              width: 1024,
              height: 1024
            }
          })
        },
        5000 // 5 second timeout
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[UnifiedAI] HuggingFace API error:', response.status, errorText);
        return null;
      }

      // HuggingFace returns binary data, convert to blob URL
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('[UnifiedAI] HuggingFace request timeout');
      } else {
        console.error('[UnifiedAI] HuggingFace error:', error);
      }
      return null;
    }
  }

  /**
   * Generate image using TogetherAI with proper timeout handling
   */
  private async generateImageTogetherAI(prompt: string, apiKey: string): Promise<string | null> {
    try {
      const response = await this.fetchWithTimeout(
        'https://api.together.xyz/v1/images/generations',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: 'black-forest-labs/FLUX.1-schnell-Free',
            prompt: prompt,
            width: 1024,
            height: 1024,
            steps: 4,
            n: 1
          })
        },
        5000 // 5 second timeout
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[UnifiedAI] TogetherAI API error:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      const imageUrl = data.data?.[0]?.url;

      if (!imageUrl) {
        console.error('[UnifiedAI] TogetherAI response missing image URL');
        return null;
      }

      // Check if the image URL is accessible
      try {
        await this.fetchWithTimeout(imageUrl, { method: 'HEAD' }, 3000);
        return imageUrl;
      } catch (error) {
        console.warn('[UnifiedAI] TogetherAI image URL not accessible:', error);
        return null;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('[UnifiedAI] TogetherAI request timeout');
      } else {
        console.error('[UnifiedAI] TogetherAI error:', error);
      }
      return null;
    }
  }

  /**
   * Fallback image generation using Pollinations (free, always works)
   */
  private async generateImagePollinations(prompt: string): Promise<string> {
    try {
      console.log('[UnifiedAI] Delegating to PollinationsService...');
      const result = await PollinationsService.getInstance().generateImage({
        prompt,
        userId: 'system-fallback',
        options: {
          model: 'flux',
          enhance: true,
          private: false
        }
      });
      return result.imageUrl;
    } catch (error) {
      console.error('[UnifiedAI] PollinationsService delegation failed:', error);
      // Absolute fallback if even the service fails (shouldn't happen due to service's own fallback)
      const seed = Math.floor(Math.random() * 100000);
      const encodedPrompt = encodeURIComponent(prompt);
      return `https://image.pollinations.ai/prompt/${encodedPrompt}?width=1024&height=1024&seed=${seed}&nologo=true`;
    }
  }

  /**
   * Generate complete story content with text, audio, and image
   */
  async generateStoryContent(
    userAction: string,
    gameState: GameState
  ): Promise<GeneratedContent> {
    const systemPrompt = `You are the narrator of a cyberpunk RPG called Lucid Cyber.
Create immersive, atmospheric descriptions of a dystopian future.
Keep responses concise (2-3 sentences) and end with 3 action choices.
Include vivid descriptions of neon lights, technology, and urban decay.`;

    const contextPrompt = `
Current Scene: ${gameState.currentScene || 'City Streets'}
Player Health: ${gameState.health}
Player Energy: ${gameState.energy}
User Action: ${userAction}

Generate a story continuation with 3 choices.`;

    // Generate text content
    const text = await this.generateText(contextPrompt, systemPrompt);

    // Parse choices from text (or generate defaults)
    const content: GeneratedContent = {
      text: this.extractStoryText(text)
    };

    // Generate audio if available
    if (CONFIG.ELEVENLABS_API_KEY) {
      content.audioUrl = await this.generateAudio(content.text, 'narrator') || undefined;
    }

    // Generate scene image
    const imagePrompt = this.extractImagePrompt(text, gameState.currentScene);
    content.imageUrl = await this.generateImage(imagePrompt) || undefined;

    return content;
  }

  /**
   * Generate combat narrative
   */
  async generateCombatNarrative(
    action: string,
    combatState: CombatState
  ): Promise<string> {
    const systemPrompt = `You manage combat encounters in a cyberpunk RPG.
Describe combat actions cinematically in 1-2 sentences.
Create tense, action-packed combat descriptions.`;

    const contextPrompt = `
Combat Action: ${action}
Player Health: ${combatState.playerHealth}/${combatState.playerMaxHealth}
Enemy Health: ${combatState.enemyHealth}/${combatState.enemyMaxHealth}
Turn: ${combatState.currentTurn}

Describe the combat action result.`;

    const narrative = await this.generateText(contextPrompt, systemPrompt);

    // Generate audio with combat agent if available
    if (CONFIG.ELEVENLABS_API_KEY) {
      try {
        await this.generateAudio(narrative, 'combat');
      } catch (error) {
        console.warn('Failed to generate combat audio:', error);
      }
    }

    return narrative;
  }

  /**
   * Generate NPC dialogue
   */
  async generateNPCDialogue(
    npcType: string,
    context: string,
    playerChoice?: string
  ): Promise<string> {
    const systemPrompt = `You voice NPCs in a cyberpunk world.
${npcType === 'vendor' ? 'Speak rough with street slang.' : ''}
${npcType === 'corporate' ? 'Speak formally with corporate jargon.' : ''}
${npcType === 'hacker' ? 'Use tech jargon and l33t speak.' : ''}
Keep responses to 1-2 sentences.`;

    const contextPrompt = `
NPC Type: ${npcType}
Context: ${context}
Player says: ${playerChoice || 'approaches silently'}

Respond as this NPC.`;

    const dialogue = await this.generateText(contextPrompt, systemPrompt);

    // Generate audio with NPC agent if available
    if (CONFIG.ELEVENLABS_API_KEY) {
      try {
        await this.generateAudio(dialogue, 'npc');
      } catch (error) {
        console.warn('Failed to generate NPC audio:', error);
      }
    }

    return dialogue;
  }

  /**
   * Helper: Clean text response from formatting artifacts
   */
  private cleanTextResponse(text: string): string {
    // Remove markdown code blocks
    text = text.replace(/```json\s*/g, '').replace(/```\s*/g, '');
    // Remove excessive whitespace
    text = text.trim().replace(/\s+/g, ' ');
    return text;
  }

  /**
   * Helper: Extract story text from response
   */
  private extractStoryText(response: string): string {
    // Remove choice listings if present
    const lines = response.split('\n');
    const storyLines = lines.filter(line =>
      !line.match(/^\d+[\.\)]/i) &&
      !line.toLowerCase().includes('choice')
    );
    return storyLines.join(' ').trim() || response;
  }

  /**
   * Helper: Extract image prompt from text
   */
  private extractImagePrompt(text: string, scene?: string): string {
    const basePrompt = scene || 'cyberpunk city';
    const keywords = text.match(/(neon|rain|street|tech|cyber|dark|night)/gi) || [];
    return `${basePrompt}, ${keywords.join(', ')}, futuristic atmosphere, cinematic lighting`;
  }

  /**
   * Fallback response generator
   */
  private getFallbackResponse(prompt: string): string {
    const inputLower = prompt.toLowerCase();

    if (inputLower.includes('combat') || inputLower.includes('fight')) {
      return 'Your cybernetic enhancements surge with power as you engage the enemy. The air crackles with digital warfare.';
    }

    if (inputLower.includes('explore') || inputLower.includes('move')) {
      return 'The neon-lit streets stretch before you, data streams flowing through the air like digital rivers. Three paths emerge from the urban maze.';
    }

    return 'The city pulses with digital life around you. Your neural interface highlights three potential actions in your augmented vision.';
  }

  /**
   * Speech-to-Text using browser Web Speech API
   */
  async speechToText(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        reject(new Error('Speech recognition not supported'));
        return;
      }

      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        resolve(transcript);
      };

      recognition.onerror = (event: any) => {
        reject(new Error(`Speech recognition error: ${event.error}`));
      };

      recognition.start();

      // Timeout after 5 seconds
      setTimeout(() => {
        recognition.stop();
      }, 5000);
    });
  }
}

export { UnifiedAIService };
export const unifiedAIService = UnifiedAIService.getInstance();